import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

type IndustryId = "manufacturing" | "retail" | "finance" | "health" | "it" | "logistics" | "consulting";
type ChallengeId = "quality" | "manual" | "forecast" | "margin" | "compliance" | "growth" | "integration";

const INDUSTRIES: Array<{ id: IndustryId; label: string; kpis: string[] }> = [
  { id: "manufacturing", label: "Produktion & Fertigung", kpis: ["OEE", "Ausschussquote", "Liefertermintreue", "Deckungsbeitrag je Linie"] },
  { id: "retail", label: "Handel & Retail", kpis: ["Warenumschlag", "Rohertrag", "Conversion", "Bestandsreichweite"] },
  { id: "finance", label: "Banken & Finanzdienstleister", kpis: ["Cost-Income-Ratio", "Churn-Risiko", "Bearbeitungszeit", "NPL-Quote"] },
  { id: "health", label: "Gesundheitswesen & Pharma", kpis: ["Auslastung", "Durchlaufzeit", "Qualitätsabweichung", "Compliance-Fälle"] },
  { id: "it", label: "IT & Technologie", kpis: ["ARR", "Net Revenue Retention", "Ticket-Cycle-Time", "Cloud-Kostenquote"] },
  { id: "logistics", label: "Logistik & Transport", kpis: ["On-Time-Delivery", "Kosten je Sendung", "Leerkilometer", "Schadensquote"] },
  { id: "consulting", label: "Beratung & Services", kpis: ["Utilization", "Projektmarge", "Pipeline Coverage", "Time-to-Staff"] },
];

const CHALLENGES: Array<{ id: ChallengeId; label: string; impact: number }> = [
  { id: "quality", label: "Datenqualität und widersprüchliche Zahlen", impact: 18 },
  { id: "manual", label: "Manuelle Excel-/Reporting-Prozesse", impact: 22 },
  { id: "forecast", label: "Schwache Forecasts und späte Warnsignale", impact: 20 },
  { id: "margin", label: "Marge, Pricing und Profitabilität", impact: 17 },
  { id: "compliance", label: "Audit, Governance und EU AI Act", impact: 14 },
  { id: "growth", label: "Wachstum, Vertrieb und Retention", impact: 16 },
  { id: "integration", label: "Datensilos zwischen ERP, CRM, HR und Web", impact: 19 },
];

export default function PotentialAnalysisPage() {
  const [industry, setIndustry] = useState<IndustryId>("manufacturing");
  const [companySize, setCompanySize] = useState("201-500");
  const [maturity, setMaturity] = useState(2);
  const [challenges, setChallenges] = useState<ChallengeId[]>(["quality", "manual"]);

  const selectedIndustry = INDUSTRIES.find((i) => i.id === industry) ?? INDUSTRIES[0];
  const result = useMemo(() => {
    const challengeScore = challenges.reduce((sum, id) => sum + (CHALLENGES.find((c) => c.id === id)?.impact ?? 0), 0);
    const maturityGap = Math.max(0, 6 - maturity) * 8;
    const potential = Math.min(94, 34 + challengeScore + maturityGap);
    const readiness = Math.max(18, Math.min(92, maturity * 17 - challenges.length * 3 + 28));
    const first90 = [
      "Metric Contract für 8 Kern-KPIs definieren",
      "Datenqualität-Gate für ERP/CRM einführen",
      "C-Level Wochenbriefing mit Evidenz-Links pilotieren",
    ];
    if (challenges.includes("manual")) first90.unshift("Monatsreporting-Automation als ersten HITL-Workflow bauen");
    if (challenges.includes("forecast")) first90.push("Forecast/Anomaly Agent auf 3 kritische Kennzahlen begrenzen");
    return { potential, readiness, first90: first90.slice(0, 4) };
  }, [challenges, maturity]);

  const toggleChallenge = (id: ChallengeId) => {
    setChallenges((current) => current.includes(id) ? current.filter((c) => c !== id) : [...current, id].slice(0, 5));
  };

  return (
    <section className="flow-page potential-page">
      <header className="flow-hero">
        <div>
          <p className="hero-eyebrow">KI-Potenzialanalyse</p>
          <h1>Vom Bauchgefühl zum priorisierten KPI- und KI-Fahrplan.</h1>
          <p className="hero-sub">
            Die Referenz-App führt über Branche, Herausforderungen, Reifegrad und Ergebnis. Hier ist dieser Flow direkt im DGX-Produkt sichtbar.
          </p>
        </div>
        <div className="flow-score">
          <span>Potenzial</span>
          <strong>{result.potential}%</strong>
          <small>Readiness {result.readiness}%</small>
        </div>
      </header>

      <div className="flow-grid">
        <section className="flow-panel">
          <div className="flow-step-label">Schritt 1</div>
          <h2>Branche & Profil</h2>
          <div className="choice-grid compact">
            {INDUSTRIES.map((item) => (
              <button key={item.id} className={`choice-card ${industry === item.id ? "active" : ""}`} onClick={() => setIndustry(item.id)}>
                {item.label}
              </button>
            ))}
          </div>
          <label className="field-row">
            <span>Unternehmensgröße</span>
            <select value={companySize} onChange={(e) => setCompanySize(e.target.value)}>
              <option>1-50</option>
              <option>51-200</option>
              <option>201-500</option>
              <option>501-1000</option>
              <option>1000+</option>
            </select>
          </label>
        </section>

        <section className="flow-panel">
          <div className="flow-step-label">Schritt 2</div>
          <h2>Herausforderungen</h2>
          <div className="choice-grid">
            {CHALLENGES.map((item) => (
              <button key={item.id} className={`choice-card ${challenges.includes(item.id) ? "active" : ""}`} onClick={() => toggleChallenge(item.id)}>
                {item.label}
              </button>
            ))}
          </div>
          <p className="muted small">Maximal 5 auswählen. Ergebnis und Priorisierung reagieren sofort.</p>
        </section>

        <section className="flow-panel">
          <div className="flow-step-label">Schritt 3</div>
          <h2>Digitaler Reifegrad</h2>
          <div className="scale-control">
            {[1, 2, 3, 4, 5].map((v) => (
              <button key={v} className={maturity === v ? "active" : ""} onClick={() => setMaturity(v)}>{v}</button>
            ))}
          </div>
          <div className="scale-labels"><span>niedrig</span><span>hoch</span></div>
        </section>

        <section className="flow-panel result-panel">
          <div className="flow-step-label">Ergebnis</div>
          <h2>Empfohlene Startarchitektur</h2>
          <div className="result-bars">
            <div><span>KI-Potenzial</span><strong style={{ width: `${result.potential}%` }} /></div>
            <div><span>Daten-Readiness</span><strong style={{ width: `${result.readiness}%` }} /></div>
          </div>
          <h3>Erste KPI-Kandidaten für {selectedIndustry.label}</h3>
          <ul className="inline-list">
            {selectedIndustry.kpis.map((kpi) => <li key={kpi}>{kpi}</li>)}
          </ul>
          <h3>90-Tage-Fokus</h3>
          <ol className="action-list">
            {result.first90.map((item) => <li key={item}>{item}</li>)}
          </ol>
          <div className="flow-actions">
            <Link to="/app/onboarding" className="btn primary">Geführte Einführung starten</Link>
            <Link to="/app/roi" className="btn ghost">ROI simulieren</Link>
          </div>
        </section>
      </div>
    </section>
  );
}
